import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PlanDetailsPage: React.FC = () => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [pdfVersion, setPdfVersion] = useState(Date.now());
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchPdf = async () => {
      setLoadingPdf(true);
      setPdfError(null);
      try {
        const response = await fetch(`/api/plan-details/pdf?v=${pdfVersion}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/pdf'
          }
        });

        if (!active) return;

        const contentType = response.headers.get('content-type');
        if (response.ok && contentType && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
        } else {
          setPdfUrl(null);
          setPdfError('Business plan document not found or unavailable.');
        }
      } catch (error) {
        if (active) {
          setPdfError('Failed to load business plan document.');
        }
      } finally {
        if (active) setLoadingPdf(false);
      }
    };

    fetchPdf();

    return () => { active = false; if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); };
  }, [pdfVersion]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        setMessage({ type: 'error', text: 'Please upload a PDF file.' });
        return;
      }

      const formData = new FormData();
      formData.append('pdf', file);

      setUploading(true);
      setMessage(null);

      try {
        const response = await fetch('/api/admin/plan-details/upload', {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || 'Upload failed');
        }

        setMessage({ type: 'success', text: 'Plan details PDF uploaded successfully.' });
        setPdfVersion(Date.now());
      } catch (error) {
        setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Upload failed' });
      } finally {
        setUploading(false);
        e.target.value = ''; // Reset input
      }
    }
  };

  return (
    <div style={{ padding: '20px', height: 'calc(100vh - 60px)', boxSizing: 'border-box' }}>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: '#2c3e50', fontSize: '1.1rem', margin: 0 }}>Business Plan Document</h3>
                {pdfUrl && (
                  <a 
                    href={pdfUrl} 
                    download="business-plan.pdf"
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center',
                      padding: '8px 16px', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      textDecoration: 'none', 
                      borderRadius: '4px', 
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    Download PDF
                  </a>
                )}
            </div>
            <div style={{ flex: 1, width: '100%', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f8f9fa' }}>
                {loadingPdf ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6c757d' }}>
                    Loading document...
                  </div>
                ) : pdfUrl ? (
                  <iframe 
                      src={`${pdfUrl}#toolbar=0&navpanes=0`}
                      title="Business Plan"
                      width="100%" 
                      height="100%"
                      style={{ border: 'none' }}
                  />
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#dc3545', flexDirection: 'column' }}>
                    <p>{pdfError || 'Document not available.'}</p>
                  </div>
                )}
            </div>
        </div>

        {user?.role === 'admin' && (
            <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px', backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', flexShrink: 0 }}>
                <h3 style={{ marginTop: 0, color: '#2c3e50', fontSize: '1.1rem' }}>Admin: Upload New Plan PDF</h3>
                <div style={{ marginBottom: '15px' }}>
                  <input 
                      type="file" 
                      accept="application/pdf" 
                      onChange={handleFileChange} 
                      disabled={uploading}
                      id="pdf-upload"
                      style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="pdf-upload" 
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Select PDF File'}
                  </label>
                  <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#6c757d' }}>
                    {uploading ? 'Please wait...' : 'Max size: 5MB'}
                  </span>
                </div>
                {message && (
                    <div style={{ 
                        padding: '10px', 
                        borderRadius: '4px', 
                        backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: message.type === 'success' ? '#155724' : '#721c24',
                        fontSize: '0.9rem'
                    }}>
                        {message.text}
                    </div>
                )}
            </div>
        )}
      </div>
    </div>
  );
};

export default PlanDetailsPage;